import { StarRate } from '@mui/icons-material'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useGetIdentity } from '@refinedev/core'
import { useState } from 'react'

import { useCreateScoutOsaRatingMutation } from '../../../api/appointments.queries'
import { SCOUT_RATING_CATEGORIES } from '../../../types'

interface RateScoutDialogProps {
  open: boolean
  onClose: () => void
  appointmentId: string
  scoutUUID: string
  scoutName?: string | null
}

const RATING_ROWS = SCOUT_RATING_CATEGORIES

type RatingKey = (typeof RATING_ROWS)[number]['key']
type RatingValues = Record<RatingKey, number | null>

const EMPTY_RATINGS: RatingValues = {
  photoSketchRating: null,
  roomCaptureRating: null,
  overallExpertiseRating: null,
}

export const RateScoutDialog = ({
  open,
  onClose,
  appointmentId,
  scoutUUID,
  scoutName,
}: RateScoutDialogProps) => {
  const [ratings, setRatings] = useState<RatingValues>(EMPTY_RATINGS)
  const [note, setNote] = useState('')

  const { data: identity } = useGetIdentity<{ email: string }>()
  const createRatingMutation = useCreateScoutOsaRatingMutation()

  const allRated = RATING_ROWS.every(row => ratings[row.key] !== null)
  const isSubmitDisabled = !allRated || !identity?.email || createRatingMutation.isPending

  const handleClose = () => {
    setRatings(EMPTY_RATINGS)
    setNote('')
    createRatingMutation.reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!allRated || !identity?.email) return
    try {
      await createRatingMutation.mutateAsync({
        appointmentId,
        data: {
          scoutUUID,
          userEmail: identity.email,
          photoSketchRating: ratings.photoSketchRating as number,
          roomCaptureRating: ratings.roomCaptureRating as number,
          overallExpertiseRating: ratings.overallExpertiseRating as number,
          note: note.trim() || undefined,
        },
      })
      handleClose()
    } catch (error) {
      console.error('Failed to create scout rating:', error)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarRate color="primary" />
        Rate Data{scoutName ? `: ${scoutName}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {RATING_ROWS.map(row => (
            <Stack key={row.key} spacing={0.5}>
              <Typography variant="body2" fontWeight={600}>
                {row.label}
              </Typography>
              <Rating
                name={row.key}
                value={ratings[row.key]}
                onChange={(_, value) => setRatings(prev => ({ ...prev, [row.key]: value }))}
                disabled={createRatingMutation.isPending}
                size="large"
              />
            </Stack>
          ))}

          <TextField
            label="Notiz für das Team"
            value={note}
            onChange={e => setNote(e.target.value)}
            disabled={createRatingMutation.isPending}
            multiline
            minRows={3}
            fullWidth
          />

          {createRatingMutation.isError && (
            <Alert severity="error">
              Failed to save rating. Please try again.
              {createRatingMutation.error?.message && `: ${createRatingMutation.error.message}`}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createRatingMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitDisabled}
          startIcon={createRatingMutation.isPending ? <CircularProgress size={20} /> : <StarRate />}
        >
          {createRatingMutation.isPending ? 'Saving...' : 'Save Rating'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
